import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap'; // ✅ Spinner for loader
import Input from 'shared/form/Input/Input';
import style from './Tasks.module.css';
import Task from './Task';
import plusIcon from 'assets/img/plus.svg';
import DueDateSelection from './DueDateSelection';
import AssignSelection from './AssignSelection';
import Select from 'react-select';
import { hasOnlySpecialCharacters } from '../../utils/general';
import DeleteModal from 'components/DeleteModal';
import { CustomToggleforChecklist, getPlainText, hasLink, normalizePastedText, titleLimit } from 'utils/Helpers';
import APIServices from 'services/APIServices';
import ReactQuill from 'react-quill';
import ConfirmModal from 'components/ConfirmModal';
import boardToast from '../../utils/boardToast';
import { TOAST_TYPE } from 'utils/AppToast';
import { useAuth } from 'contexts/AuthContext';
import ArrowLeft from 'assets/img/calender-arrow-left.svg';
import CustomDatepicker from '../../shared/CustomDatepicker/CustomDatepicker';
import dotsThreeIcon from 'assets/img/DotsThree.svg';
import delete3 from 'assets/img/delete3.svg';
import renameIcon from 'assets/img/rename.svg';
import arrowUp from 'assets/img/arrowmoveup.svg';
import downloadIcon2 from 'assets/img/download-icon2.svg';
import { Dropdown } from 'react-bootstrap';
import { useSocket } from '../../context/SocketContext';
import { Droppable, Draggable } from '@hello-pangea/dnd';

export default function ChecklistSection({
  dragHandleProps,
  index,
  section,
  tasks,
  handleAddTask,
  handleChecked,
  viewMode,
  members,
  statusOptions,
  handleDelete,
  fetchSubtask,
  isDeleting,
  reminderOptions,
  milestoneId,
  projectBoardId,
  dropdownOpen,
  onDropdownToggle,
  onMoveUp,
  onMoveDown,
  showSubTask,
  subtaskId,
  totalSections
}) {
  const socket = useSocket();
  const reactQuillRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const [error, setError] = useState('');
  const [editableTaskId, setEditableTaskId] = useState(null);
  const [editedTask, setEditedTask] = useState(null); // holds temp changes
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(section.name);
  const addItem = () => {
    setNewItem({
      name: '',
      dueDate: null,
      status: statusOptions[0]?.value, // default to "to-do"
      assignees: [],
      originalUrl: '', // ✅ store link separately
      parentChecklistId: section._id
    });
    setEditableTaskId(null);
    setEditedTask(null);
  };

  const handleCancel = () => {
    setNewItem(null);
    setError('');
    setEditableTaskId(null);
    setEditedTask(null);
  };

const handleAddTasks = async (isEntered = false) => {
  const quill = reactQuillRef.current?.getEditor();
  const plainText = quill?.getText()?.trim();

  if (!plainText) {
    setError('Please enter value');
    return;
  }

  const lines = plainText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (!lines.length) {
    setError('Please enter value');
    return;
  }

  if (lines[0].length < 2) {
    setError('Min 2 character required');
    return;
  }

  if (hasOnlySpecialCharacters(lines[0])) {
    setError('The input contains only special characters. Please enter valid text.');
    return;
  }

  // ✅ helper delay
  const wait = (ms) => new Promise((res) => setTimeout(res, ms));

  // ✅ add one-by-one with 100ms gap
  for (const line of lines) {
    handleAddTask({
      ...newItem,
      name: line
    });

    await wait(100); // ⭐ your requested interval
  }

  // ✅ reset input
  if (isEntered) {
    setNewItem({
      name: '',
      dueDate: null,
      status: statusOptions[0]?.value,
      assignees: [],
      originalUrl: '',
      parentChecklistId: section._id
    });

    requestAnimationFrame(() => {
      reactQuillRef.current?.getEditor()?.focus();
      inputWrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    });
  } else {
    setNewItem(null);
  }

  setError('');
};

  const handleDeleteItem = () => {
    setLoading(true);
    handleDelete(itemToDelete);
    setItemToDelete(null);
    setLoading(false);
    setShowConfirm(false);
  };

  const handleDueDate = (date, kbDueDateReminders = []) => {
    let dueDateString = '';
    const validDate = date === null ? null : date instanceof Date ? date : new Date(date);
    if (validDate) {
      dueDateString = validDate.toLocaleString('en-US', {
        month: 'short', // "Nov"
        day: 'numeric', // "20"
        hour: 'numeric', // "8"
        minute: '2-digit', // "00"
        hour12: true // "PM"
      });
    }
    // if (kbDueDateReminderSet) {
    setNewItem({ ...newItem, assignees: [...newItem.assignees, user._id] });
    // }
    setNewItem({
      ...newItem,
      dueDate: date instanceof Date ? date : new Date(date),
      dueDateString,
      kbDueDateReminders
    });
  };

  const handleStatusChange = (status) => {
    setNewItem({ ...newItem, status: status.value });
  };

  const handleMember = (data) => {
    if (newItem.assignees?.length > 0) {
      if (newItem.assignees.find((value) => value._id !== data._id)) {
        setNewItem({ ...newItem, assignees: [...newItem.assignees, data] });
      }
    } else {
      setNewItem({ ...newItem, assignees: [data] });
    }
  };

  const handleRemove = (userId) => {
    const filterTeam = newItem.assignees?.filter((value) => value._id !== userId);
    setNewItem({ ...newItem, assignees: filterTeam });
  };

  const handleLinkPreview = async (url, index) => {
    try {
      setIsLoading(true);
      const response = await APIServices.post('/link/preview', { url });

      if (response?.title) {
        const quill = reactQuillRef.current.getEditor();

        // Remove the raw URL
        quill.deleteText(index, url.length);

        // Insert the fetched title as a hyperlink
        quill.insertText(index, response.title, { link: url });
      }
    } catch (err) {
      console.error('Error fetching link preview:', err);
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (!reactQuillRef.current) return;

    const quill = reactQuillRef.current.getEditor();

    const handlePaste = (e) => {
      e.preventDefault();

      const text = e.clipboardData.getData('text/plain');
      if (!text) return;

      const normalized = normalizePastedText(text);

      const range = quill.getSelection(true);
      quill.insertText(range.index, normalized);
      setTimeout(() => {
        
        quill.setSelection(range.index + normalized.length);
      }, 50);
    };

    quill.root.addEventListener('paste', handlePaste);

    return () => {
      quill.root.removeEventListener('paste', handlePaste);
    };
  }, [newItem]);

  const handleTextChange = (content, delta, source, editor) => {
    const plainText = editor.getText().trim();

    // if (plainText.length > titleLimit) {
    //   const truncated = plainText.substring(0, titleLimit);

    //   // force truncate content in editor
    //   const quill = reactQuillRef.current.getEditor();
    //   quill.deleteText(0, quill.getLength());
    //   quill.insertText(0, truncated);

    //   // handleTitleChange(truncated);
    //   return;
    // }
    setNewItem((prev) => ({ ...prev, name: content }));

    if (source === 'user') {
      const matches = [...plainText.matchAll(urlRegex)];
      if (matches.length > 0) {
        const lastMatch = matches[matches.length - 1];
        const url = lastMatch[0];
        const index = lastMatch.index;
        handleLinkPreview(url, index);
      }
      // }
    }
  };
  const isDeletingParentChecklist = itemToDelete && (itemToDelete.children || itemToDelete.parentChecklistId === null);
  const confirmContent = isDeletingParentChecklist ? (
    <>
      <h2 className="mb-3">Delete {getPlainText(itemToDelete?.name || '')}?</h2>
      <h3>
        Deleting this checklist will also delete all of its child checklist items.
        <br />
        <br />
        Are you sure you want to continue?
      </h3>
    </>
  ) : (
    <>
      <h2 className="mb-3">Are you sure?</h2>
      <h3>You want to delete this checklist?</h3>
    </>
  );

  const handleSaveTask = async () => {
    console.log(editedTask.name);

    const hasAnchorLink = hasLink(editedTask.name);

    let finalTitle;

    if (hasAnchorLink) {
      console.log('Contains link, sending full HTML');
      finalTitle = editedTask.name; // send full data
    } else {
      console.log('No link, sending plain text');
      finalTitle = getPlainText(editedTask.name); // send plain text
    }

    if (!finalTitle) {
      boardToast('Please enter title', TOAST_TYPE.ERROR);
      return;
    } else if (finalTitle.length < 2) {
      boardToast('Min 2 character required for title', TOAST_TYPE.ERROR);
      return;
    } else if (hasOnlySpecialCharacters(finalTitle)) {
      boardToast('The input contains only special characters. Please enter valid text.', TOAST_TYPE.ERROR);
      return;
    } else {
      handleChecked({ ...editedTask, name: finalTitle }, 'all');
      setEditableTaskId(null);
      setEditedTask(null);
    }
  };

  // Inside your Tasks component
  useEffect(() => {
    if (newItem && reactQuillRef.current) {
      const quill = reactQuillRef.current.getEditor();
      quill.focus();
    }
  }, [newItem]);

  // Regex to detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const handleDeleteChecklistSection = () => {
    setItemToDelete(section); // store section
    setShowConfirm(true);
  };

  const confirmDeleteChecklistSection = () => {
    if (!itemToDelete) return;

    if (showSubTask) {
      socket.emit(
        'delete-checklist',
        {
          id: itemToDelete._id,
          subtaskId
        },
        projectBoardId
      );
    } else {
      socket.emit(
        'delete-checklist',
        {
          id: itemToDelete._id,
          milestoneId
        },
        projectBoardId
      );
    }

    setItemToDelete(null);
    setShowConfirm(false);
  };

  const handleRenameChecklist = () => {
    const plain = renameValue.replace(/<[^>]+>/g, '').trim();
    if (!plain || plain.length < 2) return;

    if (showSubTask) {
      socket.emit(
        'update-checklist',
        {
          id: section._id,
          subtaskId,
          payload: {
            name: renameValue
          }
        },
        projectBoardId
      );
    } else {
      socket.emit(
        'update-checklist',
        {
          id: section._id,
          milestoneId,
          payload: {
            name: renameValue
          }
        },
        projectBoardId
      );
    }

    setIsRenaming(false);
  };

  const sortedTasks = [...(tasks || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const isFirst = index === 0;
  const isLast = index === totalSections - 1;

  useEffect(() => {
    if (newItem) {
      requestAnimationFrame(() => {
        inputWrapperRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      });
    }
  }, [newItem]);

  let count = sortedTasks?.filter((x) => x?.isCompleted)?.length;
  let totalCount = sortedTasks?.length;

  return (
    <div className={`${style.tasks_wrapper} ${style.checklist_wrapper} wrapped_checks`}>
      <div className={style.checklist_top_header} {...dragHandleProps} style={{ cursor: 'grab' }}>
        {!isRenaming ? (
          <label>
            {getPlainText(section.name)} ({count}/{totalCount})
          </label>
        ) : (
          <input
            type="text"
            className={style.containername_input}
            value={renameValue}
            autoFocus
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameChecklist();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setIsRenaming(false);
              }
            }}
            onBlur={handleRenameChecklist} // optional: auto-save on blur
          />
        )}

        {!viewMode && (
          <div className={style.actions_drp}>
            <Dropdown
              align="end"
              show={dropdownOpen} // controlled by parent
              onToggle={(isOpen) => onDropdownToggle(isOpen)} // notify parent
            >
              <Dropdown.Toggle as={CustomToggleforChecklist} id="dropdown-custom-components">
                <img src={dotsThreeIcon} alt="menu" />
              </Dropdown.Toggle>

              <Dropdown.Menu className={style.dropdown_menu}>
                <Dropdown.Item onClick={() => setIsRenaming(true)}>
                  <img src={renameIcon} alt="rename" className="featherdrowpdown-icon" />
                  Rename
                </Dropdown.Item>

                {/* {!isFirst && (
                  <Dropdown.Item onClick={onMoveUp}>
                    <img src={arrowUp} alt="move-up" className="featherdrowpdown-icon move-icon" />
                    Move Up
                  </Dropdown.Item>
                )} */}

                {/* {!isLast && (
                  <Dropdown.Item onClick={onMoveDown}>
                    <img
                      src={arrowUp}
                      style={{ rotate: '180deg' }}
                      alt="move-down"
                      className="featherdrowpdown-icon move-icon"
                    />
                    Move Down
                  </Dropdown.Item>
                )} */}

                <Dropdown.Divider />

                <Dropdown.Item onClick={handleDeleteChecklistSection}>
                  <img src={delete3} alt="delete" className="featherdrowpdown-icon" />
                  Delete Checklist
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
      <div className={style.task_content_cover}>
        <Droppable droppableId={section._id} type="CHECKLIST_ITEM">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} style={{ minHeight: '8px' }}>
              {sortedTasks?.map((value, index) => (
                <Draggable key={value._id} draggableId={value._id} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                      <Task
                        reminderOptions={reminderOptions}
                        milestone={value}
                        isCompleted={value.isCompleted}
                        dropdownOpen={openDropdownId === value?._id}
                        onDropdownToggle={(isOpen) => setOpenDropdownId(isOpen ? value._id : null)}
                        title={editableTaskId === value._id && editedTask ? editedTask?.name : value.name}
                        dueDate={
                          (editableTaskId === value._id && editedTask ? editedTask?.dueDate : value.dueDate)
                            ? new Date(editableTaskId === value._id && editedTask ? editedTask?.dueDate : value.dueDate)
                            : null
                        }
                        assignees={editableTaskId === value._id && editedTask ? editedTask?.assignees : value.assignees}
                        handleChecked={(e) => {
                          setEditedTask((prev) => ({
                            ...prev,
                            isCompleted: e.target.checked
                          }));
                          handleChecked({ ...value, isCompleted: e.target.checked }, 'isCompleted');
                        }}
                        disabled={viewMode}
                        statusOptions={statusOptions}
                        status={
                          editableTaskId === value._id && editedTask
                            ? editedTask?.status?._id || editedTask?.status
                            : value?.status?._id
                              ? value?.status?._id
                              : value?.status
                        }
                        isSubTask={false}
                        isEditable={editableTaskId === value._id}
                        handleStatusChange={(status) => {
                          setEditedTask((prev) => ({ ...prev, status }));
                          handleChecked({ ...value, status }, 'status');
                        }}
                        handleDueDateChange={(date, kbDueDateReminders = []) => {
                          // console.log(kbDueDateReminderSet, kbDueDateReminderId);

                          let dueDateString = '';
                          const validDate = date === null ? null : date instanceof Date ? date : new Date(date);
                          if (validDate) {
                            dueDateString = validDate.toLocaleString('en-US', {
                              month: 'short', // "Nov"
                              day: 'numeric', // "20"
                              hour: 'numeric', // "8"
                              minute: '2-digit', // "00"
                              hour12: true // "PM"
                            });
                          }
                          const prevIds = (value?.assignees || []).map((a) => (typeof a === 'string' ? a : a._id));

                          setEditedTask((prev) => ({
                            ...prev,
                            dueDate: validDate,
                            dueDateString,
                            kbDueDateReminders
                          }));
                          handleChecked({ ...value, dueDate: validDate, dueDateString, kbDueDateReminders }, 'dueDate');
                          if (!prevIds.includes(user._id) && kbDueDateReminders?.[0]?.kbDueDateReminderSet) {
                            setEditedTask((prev) => ({
                              ...prev,
                              assignees: [...prevIds, user._id]
                            }));
                            handleChecked({ ...value, assignees: [...prevIds, user._id] }, 'assignees');
                          }
                        }}
                        members={members}
                        fetchSubtask={() => fetchSubtask(value._id)}
                        handleTitleChange={(newTitle) => {
                          setEditedTask((prev) => ({
                            ...prev,
                            name: newTitle
                          }));
                        }}
                        handleAddAssignee={(memberId) => {
                          const prevIds = (value?.assignees || []).map((a) => (typeof a === 'string' ? a : a._id));
                          if (prevIds.includes(memberId)) return value; // already added
                          setEditedTask((prev) => ({
                            ...prev,
                            assignees: [...prevIds, memberId]
                          }));

                          handleChecked({ ...value, assignees: [...prevIds, memberId] }, 'assignees');
                        }}
                        handleRemoveAssignee={(memberId) => {
                          const prevIds = value?.assignees || [];
                          const remainingIds = prevIds.filter((x) => {
                            if (typeof x === 'string') return x !== memberId;
                            if (typeof x === 'object' && x !== null) return x._id !== memberId;
                            return true;
                          });

                          setEditedTask((prev) => ({
                            ...prev,
                            assignees: remainingIds
                          }));

                          handleChecked({ ...value, assignees: remainingIds }, 'assignees');
                        }}
                        handleDisableTask={() => {
                          setEditableTaskId(null);
                          setEditedTask(null);
                        }}
                        handleSetEditableTask={() => {
                          setEditableTaskId(value._id);
                          setEditedTask(value);
                        }}
                        handleSaveTask={handleSaveTask}
                        cancelNewItem={() => setNewItem(null)}
                        handleDeleteTask={() => {
                          setItemToDelete(value);
                          setShowConfirm(true);
                        }}
                      />

                      {editableTaskId === value._id && (
                        <div className="w-100">
                          <div className="editdelete-text">
                            <div className="d-flex gap-2">
                              <Button variant="success" onClick={handleSaveTask}>
                                Save
                              </Button>
                              <Button variant="link" onClick={() => setEditableTaskId(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      {!viewMode && (
        <>
          {!newItem && (
            <Button variant="light" className={style.add_item_button} onClick={addItem}>
              <div className="icon-btn">
                <img src={plusIcon} alt="search-icon" className="search-icon-right" />
              </div>
              {'New checklist item'}
            </Button>
          )}
          {newItem && (
            <div ref={inputWrapperRef} className="w-100">
              <>
                <ReactQuill
                  className={`${style.quill_input}`}
                  title={newItem.name}
                  ref={reactQuillRef}
                  value={newItem.name || ''}
                  onChange={handleTextChange}
                  placeholder="Add an item"
                  modules={{ toolbar: false }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTasks(true);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancel();
                    }
                  }}
                />

                {isLoading && (
                  <div className="mt-2 d-flex align-items-center text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Fetching title…
                  </div>
                )}
              </>

              <div className="mt-2 d-flex justify-content-between add_subtask">
                <div className="d-flex gap-2">
                  <Button variant="success" onClick={handleAddTasks}>
                    Save
                  </Button>
                  <Button variant="link" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>

                <div className="d-flex gap-2 add_subtask_asign">
                  <CustomDatepicker
                    defaultDate={newItem?.dueDate}
                    handleDueDate={handleDueDate}
                    reminderOptionsData={reminderOptions}
                    // milestone={milestone}
                    showDateOnly={true}
                  />
                  <div className="milestone-task-checklist">
                    <AssignSelection
                      assignees={newItem?.assignees}
                      members={members}
                      handleMember={handleMember}
                      handleRemove={handleRemove}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmModal
        show={showConfirm}
        onHide={() => {
          setShowConfirm(false);
          setItemToDelete(null);
        }}
        confirmationText={confirmContent}
        // handleAction={handleDeleteItem}
        handleAction={confirmDeleteChecklistSection}
        closeText="Yes"
        loading={isDeleting}
        bgClassname={'whitebgmodal'}
      />
    </div>
  );
}
